import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, Clock, Copy, MessageSquareHeart, Phone } from 'lucide-react';
import './customer-b.css';

const CUSTOMER_B_BASE = '/customer/b';
const HERO_IMAGE_SRC = '/images/customer-b/hero-family.png';
const BANK = {
  bank: '신한은행',
  account: '100-035-387966',
  holder: '(주)제제미미',
  amount: 69000,
} as const;

type CustomerBScreen = 'landing' | 'payment' | 'form';
type RequestType = 'gift_direct' | 'phone_consult';

const normalizePathname = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

const getPageQuery = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return new URLSearchParams(window.location.search).get('page');
  } catch {
    return null;
  }
};

const resolveScreen = (pathname: string): CustomerBScreen => {
  const page = getPageQuery();
  const route = page && page.startsWith('customer/b')
    ? `/${page}`
    : normalizePathname(pathname);

  if (route === `${CUSTOMER_B_BASE}/payment`) {
    return 'payment';
  }

  if (route === `${CUSTOMER_B_BASE}/form`) {
    return 'form';
  }

  return 'landing';
};

const navigateTo = (path: string): void => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

type ApplyFormProps = {
  requestType: RequestType;
  title: string;
  subtitle: string;
  submitLabel: string;
  askChildContact?: boolean;
  defaultConsult?: boolean;
};

const ApplyForm: React.FC<ApplyFormProps> = ({
  requestType,
  title,
  subtitle,
  submitLabel,
  askChildContact = false,
  defaultConsult = false,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [childContact, setChildContact] = useState('');
  const [wantsConsult, setWantsConsult] = useState(defaultConsult);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const isValid = name.trim().length >= 2 && phone.trim().length >= 1;

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = window.setTimeout(() => {
      setToast(null);
    }, 2200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [toast]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!isValid) {
      setToast('입력한 내용을 다시 확인해주세요');
      return;
    }

    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 400);
  };

  const copyAccount = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(BANK.account.replace(/-/g, ''));
      setToast('계좌번호를 복사했어요');
    } catch {
      setToast('복사하지 못했어요. 직접 적어주세요');
    }
  };

  if (submitted) {
    return (
      <div className="customer-b-page customer-b-page--done">
        {toast ? <div className="customer-b-toast" role="status">{toast}</div> : null}
        <div className="customer-b-success">
          <span className="customer-b-success-icon" aria-hidden="true">
            <Check />
          </span>
          <h1>신청이 접수됐어요</h1>
          {requestType === 'phone_consult' ? (
            <p>
              입력하신 전화번호로 연락드릴게요.
              <br />
              평일 오전 10시 ~ 오후 6시 사이에 전화드려요.
            </p>
          ) : (
            <p>
              아래 계좌로 입금해주시면
              <br />
              구독권을 바로 보내드려요.
            </p>
          )}
          <p className="customer-b-success-note">확인 알림톡을 먼저 보내드릴게요.</p>
        </div>

        {requestType === 'gift_direct' ? (
          <div className="customer-b-account">
            <p className="customer-b-account-label">입금 계좌</p>
            <p className="customer-b-account-bank">{BANK.bank}</p>
            <p className="customer-b-account-number">{BANK.account}</p>
            <p className="customer-b-account-holder">예금주 {BANK.holder}</p>
            <div className="customer-b-price-divider" />
            <p className="customer-b-account-amount">{BANK.amount.toLocaleString('ko-KR')}원</p>
            <button type="button" className="customer-b-copy" onClick={() => { void copyAccount(); }}>
              <Copy aria-hidden="true" />
              계좌번호 복사하기
            </button>
          </div>
        ) : null}

        <div className="customer-b-home-wrap">
          <button
            type="button"
            className="customer-b-btn customer-b-btn-outline"
            onClick={() => navigateTo(CUSTOMER_B_BASE)}
          >
            <ChevronLeft aria-hidden="true" />
            처음으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-b-page">
      {toast ? <div className="customer-b-toast" role="status">{toast}</div> : null}
      <button type="button" className="customer-b-back" onClick={() => navigateTo(CUSTOMER_B_BASE)}>
        <ChevronLeft aria-hidden="true" />
        뒤로가기
      </button>
      <h1 className="customer-b-page-title">{title}</h1>
      <p className="customer-b-page-lead">{subtitle}</p>
      <form className="customer-b-form" onSubmit={handleSubmit}>
        <div className="customer-b-field">
          <label htmlFor="name">성함</label>
          <input
            id="name"
            className="customer-b-input"
            maxLength={30}
            placeholder="예) 김영자"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="customer-b-field">
          <label htmlFor="phone">전화번호</label>
          <input
            id="phone"
            className="customer-b-input"
            type="tel"
            inputMode="numeric"
            maxLength={20}
            placeholder="예) 010-1234-5678"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>
        {askChildContact ? (
          <div className="customer-b-field">
            <label htmlFor="child">구독권을 받을 자녀 정보</label>
            <p className="customer-b-field-hint">자녀 휴대폰번호 또는 앱 초대코드를 적어주세요</p>
            <input
              id="child"
              className="customer-b-input"
              maxLength={40}
              placeholder="예) 010-9876-5432 또는 ABC123"
              value={childContact}
              onChange={(event) => setChildContact(event.target.value)}
            />
          </div>
        ) : null}
        <label className="customer-b-check">
          <input
            type="checkbox"
            checked={wantsConsult}
            onChange={(event) => setWantsConsult(event.target.checked)}
          />
          <span>
            <span className="customer-b-check-title">
              <Phone aria-hidden="true" />
              전화 상담을 받고 싶어요
            </span>
            <span className="customer-b-check-desc">
              평일 오전 10시 ~ 오후 6시 사이 상담원이 전화드려요
            </span>
          </span>
        </label>
        <div className="customer-b-dock">
          <div className="customer-b-dock-inner">
            <button
              type="submit"
              className="customer-b-btn customer-b-btn-primary"
              disabled={!isValid || submitting}
            >
              {submitting ? '신청 중이에요...' : submitLabel}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const GiftLanding: React.FC = () => (
  <>
    <main className="customer-b-main">
      <img
        className="customer-b-hero"
        src={HERO_IMAGE_SRC}
        alt="바쁜 육아 일상 속에서 아빠가 아기에게 이유식을 먹이고 엄마는 전화를 하며 빨래를 정리하는 모습"
        width={1200}
        height={800}
      />
      <h1 className="customer-b-title">
        육아하느라 정신없는
        <br />
        우리 딸,아들
        <br />
        이번엔 제가 챙겨줄게요
      </h1>
      <p className="customer-b-lead">
        손주 사진 광고 없이 보고
        <br />
        바쁜 딸,아들의 일을 덜어줄수 있어요!
      </p>
      <section className="customer-b-price">
        <span className="customer-b-price-badge">지금 선물하면 드리는 가격</span>
        <p className="customer-b-price-monthly">한 달에 5,900원</p>
        <p className="customer-b-price-year">
          <s>99,000원</s>
          {' '}
          → 69,000원 / 1년
        </p>
        <div className="customer-b-price-divider" />
        <p className="customer-b-price-note">가족 8명 기준 1인당 월 719원꼴이에요</p>
      </section>
      <div className="customer-b-notice">
        <p>
          <MessageSquareHeart aria-hidden="true" />
          신청하시면 확인 알림톡을 먼저 보내드려요
        </p>
        <p>
          <Clock aria-hidden="true" />
          전화 상담을 고르시면 평일 오전 10시 ~ 오후 6시 사이 쑥쑥찰칵 상담원이 전화드려요
        </p>
      </div>
      <section className="customer-b-benefits">
        <h2>특별한 구독 혜택들</h2>
        <div className="customer-b-benefit-list">
          <article className="customer-b-benefit">
            <div className="customer-b-benefit-inner">
              <span className="customer-b-benefit-index">1</span>
              <h3>온 가족이 광고 없이 편하게</h3>
              <p>
                구독 1명이면 모든 가족이
                <br />
                광고 없이 앱을 사용할 수 있어요
              </p>
            </div>
          </article>
          <article className="customer-b-benefit">
            <div className="customer-b-benefit-inner">
              <span className="customer-b-benefit-index">2</span>
              <h3>바쁜 딸/아들이 사진 정리 편하게!</h3>
              <p>
                한번에 많은 사진 업로드
                <br />
                사진 검색, 모아보기 까지 한번에
              </p>
            </div>
          </article>
          <article className="customer-b-benefit">
            <div className="customer-b-benefit-inner">
              <span className="customer-b-benefit-index">3</span>
              <h3>손주 성장영상도 예쁘게</h3>
              <p>프리미엄 성장영상, AI 쑥티콘까지</p>
            </div>
          </article>
        </div>
      </section>
    </main>
    <div className="customer-b-dock">
      <div className="customer-b-dock-inner">
        <button
          type="button"
          className="customer-b-btn customer-b-btn-primary"
          onClick={() => navigateTo(`${CUSTOMER_B_BASE}/payment`)}
        >
          지금 딸/아들에게 구독권 보내기
        </button>
        <button
          type="button"
          className="customer-b-btn customer-b-btn-outline"
          onClick={() => navigateTo(`${CUSTOMER_B_BASE}/form`)}
        >
          <Phone aria-hidden="true" />
          전화로 선물하는 법 물어보기
        </button>
      </div>
    </div>
  </>
);

export const CustomerBView: React.FC = () => {
  const [pathname, setPathname] = useState(() => (
    typeof window === 'undefined' ? CUSTOMER_B_BASE : window.location.pathname
  ));
  const screen = useMemo(() => resolveScreen(pathname), [pathname]);

  useEffect(() => {
    const titles: Record<CustomerBScreen, string> = {
      landing: '쑥쑥찰칵 구독권 선물 — 연간 30% 할인',
      payment: '구독권 선물하기 — 1년 69,000원',
      form: '전화 상담 신청 — 쑥쑥찰칵 구독권 선물',
    };
    document.title = titles[screen];
  }, [screen]);

  useEffect(() => {
    const syncPathname = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener('popstate', syncPathname);
    return () => {
      window.removeEventListener('popstate', syncPathname);
    };
  }, []);

  return (
    <div className="customer-b">
      {screen === 'payment' ? (
        <ApplyForm
          requestType="gift_direct"
          title="구독권을 선물해요"
          subtitle="1년 69,000원 (월 5,900원). 신청 후 입금 계좌를 안내드려요"
          submitLabel="구독권 선물 신청하기"
          askChildContact
        />
      ) : null}
      {screen === 'form' ? (
        <ApplyForm
          requestType="phone_consult"
          title="전화로 편하게 물어보세요"
          subtitle="성함과 전화번호만 남겨주시면 상담원이 전화로 알려드려요"
          submitLabel="전화 상담 신청하기"
          defaultConsult
        />
      ) : null}
      {screen === 'landing' ? <GiftLanding /> : null}
    </div>
  );
};
